import clsx from 'clsx'
import NextLink from 'next/link'
import { Fragment, useEffect, useState, type FC } from 'react'

import { Icon } from '@wartek-id/icon-v2'
import { ListItem } from '@wartek-id/list'
import { Text } from '@wartek-id/text-v1'

import useToggleSdkContext from 'utils/useToogleSdk'
import { useAPIGetNotificationList } from 'api/kinerja/useAPIKinerjaNotification'
import { useAPIGetPSPegawaiNotificationList } from 'api/kinerja/pengawas-sekolah/notification'
import {
  PENGELOLAAN_KINERJA_BERANDA_PATH,
  PENGELOLAAN_KINERJA_GURU_PATH,
  PENGELOLAAN_KINERJA_KADIN_PATH,
  PENGELOLAAN_KINERJA_KADIN_PEMBERI_PREDIKAT_PATH,
  PENGELOLAAN_KINERJA_KS_PEGAWAI_PATH,
  PENGELOLAAN_KINERJA_KS_PENILAI_PATH,
  PENGELOLAAN_KINERJA_NOTIFIKASI_PATH,
  PENGELOLAAN_KINERJA_PS_PATH,
  PENGELOLAAN_KINERJA_PS_PEGAWAI_PATH,
  PENGELOLAAN_KINERJA_RIWAYAT_PATH,
  PENGELOLAAN_KINERJA_TIM_KINERJA_PS_PATH,
  PENGELOLAAN_KINERJA_PEJABAT_PENILAI_KINERJA_PS_PATH,
  PENGELOLAAN_KINERJA_PAMONG_PATH,
  PENGELOLAAN_KINERJA_KSPNF_PEGAWAI_PATH,
  PENGELOLAAN_KINERJA_PENILIK_PEGAWAI_PATH,
  PENGELOLAAN_KINERJA_TK_PENILIK_PATH,
  PENGELOLAAN_KINERJA_PPK_PENILIK_PATH,
  PENGELOLAAN_KINERJA_KSPNF_PENILAI_PATH,
  PENGELOLAAN_KINERJA_GPK_PEGAWAI_PATH,
  PENGELOLAAN_KINERJA_GPK_TIM_KINERJA_PATH,
  PENGELOLAAN_KINERJA_GPK_PEJABAT_PENILAI_KINERJA_PATH,
} from 'app/KinerjaShared/constants/path'
import useRoleContext from 'app/KinerjaShared/hooks/useRoleContext'
import { PANDUAN_PENGELOLAAN_URL } from 'app/KinerjaShared/utils/panduan'
import { RoleLabel } from 'app/KinerjaShared/utils/profile'
import usePeriodContext from 'app/KinerjaShared/hooks/usePeriodContext'

import type { Dispatch, SetStateAction } from 'react'

type PageName =
  | 'beranda'
  | 'guru-pegawai'
  | 'ks-parent'
  | 'ks-pegawai'
  | 'ks-penilai'
  | 'pamong-pegawai'
  | 'kepala-spnf-parent'
  | 'kepala-spnf-pegawai'
  | 'kepala-spnf-penilai'
  | 'ps-parent'
  | 'ps-pegawai'
  | 'ps-penilai'
  | 'penilik-parent'
  | 'penilik-pegawai'
  | 'kadin-parent'
  | 'kadin-ks'
  | 'kadin-pemberi-predikat'
  | 'riwayat'
  | 'bantuan'
  | 'notifikasi'
  | 'notifikasi-ps-parent'
  | 'notifikasi-ps-pegawai'
  | 'notifikasi-ps-tim-kinerja'
  | 'notifikasi-penilik-parent'
  | 'notifikasi-penilik-pegawai'
  | 'notifikasi-penilik-tim-kinerja'
  | 'tim-kinerja-parent'
  | 'tim-kinerja-ps'
  | 'tim-kinerja-penilik'
  | 'pejabat-penilai-kinerja-ps'
  | 'pejabat-penilai-kinerja-penilik'
  | 'gpk-pegawai'
  | 'gpk-tim-kinerja'
  | 'gpk-pejabat-penilai-kinerja'

interface SidebarNavItem {
  key: PageName
  label: string
  icon?: string
  href?: string
  externalLink?: boolean
  submenus?: Array<SidebarNavItem> | undefined
}

interface KinerjaSidebarProps {
  sticky?: boolean
  activeMenu?: PageName
}

const expandAllOnFirstLoad = false

const Link = ({ href, children, ...props }) => {
  return (
    <NextLink href={href} passHref>
      <a {...props}>{children}</a>
    </NextLink>
  )
}

const MenuLink = ({
  expanded = null,
  setExpanded,
  menus,
  activeMenu,
  className = '',
  parentKey,
  parentId,
}: {
  expanded: Array<PageName>
  setExpanded: Dispatch<SetStateAction<Array<PageName>>>
  menus: Array<SidebarNavItem>
  activeMenu?: PageName
  className?: string
  parentKey?: string
  parentId?: string
}) => {
  const { periodYear } = usePeriodContext()
  const toggleValue = useToggleSdkContext()
  const {
    isLoading,
    isPengawasSekolahOnly,
    isKepalaDinas,
    isKsDefinitif,
    isKsPlt,
    isGuru,
    isProfilePSPegawaiLoading,
    isPegawaiPengawasSekolah,
    isTimKinerjaPengawasSekolah,
    isPPKPengawasSekolah,
    isPamong,
    isKsPltSpnf,
    isKsDefinitifSpnf,
    isProfilePenilikPegawaiLoading,
    isPenilikPegawai,
    isTKPenilik,
    isPPKPenilik,
    isGpkPegawai,
    isGpkTimKinerja,
  } = useRoleContext()

  if (!menus) return null

  // TODO [Kinerja]: Filter the available menus based on the toggle value
  const filteredMenus = menus.filter((menu) => {
    let allowed = true

    // Kepala Sekolah toggles
    const isKsPenilaiEnabled = toggleValue?.['kinerja-enabled']

    const isKsPegawaiEnabled =
      toggleValue?.['kinerja-ks-enabled'] ||
      toggleValue?.['kinerja-ks-pelaksanaan-enabled']

    const isKsEnabled = isKsPenilaiEnabled || isKsPegawaiEnabled

    const isKadingKSAllowed =
      toggleValue?.['kinerja-dinas-enabled'] && !isLoading && isKepalaDinas

    // Pengawas Sekolah toggles
    const isPsPenilaiEnabled = toggleValue?.['kinerja-ps-enabled']

    const isPsPegawaiEnabled =
      toggleValue?.['kinerja-ps-enabled'] &&
      toggleValue?.['kinerja-ps-pegawai-enabled']
    const isTimPenilaiPS = isTimKinerjaPengawasSekolah || isPPKPengawasSekolah
    const isNotifikasiPSPegawaiEnabled =
      toggleValue?.['kinerja-ps-pegawai-notification-enabled']

    const isPsEnabled = isPsPenilaiEnabled || isPsPegawaiEnabled

    // Kepala Dinas Pendidikan toggles
    const isKepalaDinasEnabled = toggleValue?.['kinerja-dinas-enabled']

    // GPK toggles
    const isKinerjaGpkEnabled = toggleValue?.['kinerja-gpk-enabled']

    // SPNF toggles
    const isSpnfEnabled = toggleValue?.['kinerja-spnf-enabled']
    const isPenilikPegawaiEnabled =
      toggleValue?.['kinerja-penilik-pegawai-enabled']

    // Tim Kinerja
    const isTimKinerjaPengawasSekolahAllowed =
      toggleValue?.['kinerja-ps-tim-penilai-ps-enabled'] &&
      !isProfilePSPegawaiLoading &&
      isTimKinerjaPengawasSekolah

    const isTimKinerjaGpkAllowed =
      isKinerjaGpkEnabled && !isLoading && isGpkTimKinerja
    const isTimKinerjaPenilikAllowed =
      toggleValue?.['kinerja-penilik-pegawai-enabled'] &&
      !isProfilePenilikPegawaiLoading &&
      isTKPenilik

    // Pejabat Penilai Kinerja
    const isPPKPSAllowed =
      toggleValue?.['kinerja-ps-tim-penilai-ps-enabled'] &&
      !isProfilePSPegawaiLoading &&
      isPPKPengawasSekolah
    const isPPKPenilikAllowed =
      toggleValue?.['kinerja-penilik-tim-penilai-enabled'] &&
      !isProfilePenilikPegawaiLoading &&
      isPPKPenilik

    switch (menu.key) {
      case 'guru-pegawai':
        // Display the menu if the toggle is on, the user data loaded, and the user is a Guru
        // TODO [Kinerja]: Update the condition if necessary
        allowed = toggleValue?.['kinerja-enabled'] && !isLoading && isGuru
        break

      case 'ks-parent':
        // Display the menu if the toggle is on, the user data loaded, and the user is a Kepala Sekolah (Definitif / Plt)
        allowed = isKsEnabled && !isLoading && (isKsDefinitif || isKsPlt)
        break

      case 'ks-pegawai':
        // Display the menu if the toggle is on, the user data loaded, and the user is a Kepala Sekolah (Definitif / Plt)
        allowed = isKsPegawaiEnabled && !isLoading && (isKsDefinitif || isKsPlt)
        break

      case 'ks-penilai':
        // Display the menu if the toggle is on, the user data loaded, and the user is a Kepala Sekolah (Definitif / Plt)
        // TODO [Kinerja]: Update the condition if necessary
        allowed = isKsPenilaiEnabled && !isLoading && (isKsDefinitif || isKsPlt)
        break

      case 'pamong-pegawai':
        allowed = isSpnfEnabled && !isLoading && isPamong && periodYear >= 2026
        break

      case 'kepala-spnf-parent':
        allowed =
          isSpnfEnabled &&
          !isLoading &&
          (isKsDefinitifSpnf || isKsPltSpnf) &&
          periodYear >= 2026
        break

      case 'kepala-spnf-pegawai':
        allowed =
          isSpnfEnabled &&
          !isLoading &&
          (isKsDefinitifSpnf || isKsPltSpnf) &&
          periodYear >= 2026
        break

      case 'kepala-spnf-penilai':
        allowed =
          isSpnfEnabled &&
          !isLoading &&
          (isKsDefinitifSpnf || isKsPltSpnf) &&
          periodYear >= 2026
        break

      case 'ps-parent':
        // Display the menu if the toggle is on, the user data loaded, and the user is a Pengawas Sekolah
        allowed =
          isPsEnabled &&
          ((!isProfilePSPegawaiLoading && isPegawaiPengawasSekolah) ||
            (!isLoading && isPengawasSekolahOnly))
        break

      case 'ps-pegawai':
        // Display the menu if the toggle is on, the user data loaded, and the user is a Pengawas Sekolah
        allowed =
          isPsPegawaiEnabled &&
          !isProfilePSPegawaiLoading &&
          isPegawaiPengawasSekolah
        break

      case 'ps-penilai':
        // Display the menu if the toggle is on, the user data loaded, and the user is a Pengawas Sekolah
        allowed = isPsPenilaiEnabled && !isLoading && isPengawasSekolahOnly
        break

      case 'kadin-parent':
        // Display the menu if the toggle is on, the user data loaded, and the user is a Kepala Dinas Pendidikan, or Pejabat Penilai Kinerja PS or Penilik
        allowed = isKadingKSAllowed || isPPKPSAllowed || isPPKPenilikAllowed
        break

      case 'kadin-ks':
        // Display the menu if the toggle is on, the user data loaded, and the user is a Kepala Dinas Pendidikan
        allowed = isKadingKSAllowed
        break
      case 'kadin-pemberi-predikat':
        // Display the menu if the toggle is on, the user data loaded, and the user is a Kepala Dinas Pendidikan
        allowed =
          toggleValue?.['kinerja-dinas-enabled'] &&
          !isLoading &&
          isKepalaDinas &&
          process.env.NEXT_PUBLIC_SKP_KADIN_PEMBERI_PREDIKAT_ENABLED === 'true'
        break

      case 'notifikasi':
        // Display the menu if the toggle is on
        allowed =
          process.env.NEXT_PUBLIC_SKP_NOTIFIKASI_ENABLED === 'true' &&
          (!(isPegawaiPengawasSekolah || isTimPenilaiPS) ||
            !isNotifikasiPSPegawaiEnabled)
        break

      case 'notifikasi-ps-parent':
        // Display the menu if the toggle is on
        allowed =
          process.env.NEXT_PUBLIC_SKP_NOTIFIKASI_ENABLED === 'true' &&
          isNotifikasiPSPegawaiEnabled &&
          !isProfilePSPegawaiLoading &&
          (isPegawaiPengawasSekolah || isTimPenilaiPS)
        break

      case 'riwayat':
        // Display the menu if the toggle is on, the user data loaded, and the user is a Guru or Kepala Sekolah (Definitif / Plt)
        allowed =
          toggleValue?.['kinerja-riwayat-enabled'] &&
          !isLoading &&
          (isKsDefinitif || isKsPlt || isGuru)
        break

      case 'tim-kinerja-ps':
        // Display the menu if the toggle is on, the user data loaded, and the user is a Tim Kinerja Pengawas Sekolah
        allowed = isTimKinerjaPengawasSekolahAllowed
        break
      case 'tim-kinerja-penilik':
        // Display the menu if the toggle is on, the user data loaded, and the user is a Tim Kinerja Penilik
        allowed = isTimKinerjaPenilikAllowed
        break

      case 'pejabat-penilai-kinerja-ps':
        // Display the menu if the toggle is on, the user data loaded, and the user is a Pejabat Penilai Kinerja Pengawas Sekolah
        allowed = isPPKPSAllowed
        break

      case 'pejabat-penilai-kinerja-penilik':
        // Display the menu if the toggle is on, the user data loaded, and the user is a Pejabat Penilai Kinerja Pengawas Sekolah
        allowed = isPPKPenilikAllowed
        break

      // Penilik SPNF
      case 'penilik-parent':
        allowed =
          isPenilikPegawaiEnabled &&
          !isProfilePenilikPegawaiLoading &&
          isPenilikPegawai
        break

      case 'penilik-pegawai':
        allowed =
          isPenilikPegawaiEnabled &&
          !isProfilePenilikPegawaiLoading &&
          isPenilikPegawai
        break
      case 'tim-kinerja-parent':
        allowed =
          isTimKinerjaPengawasSekolahAllowed ||
          isTimKinerjaGpkAllowed ||
          isTimKinerjaPenilikAllowed
        break
      case 'gpk-pegawai':
        allowed =
          isKinerjaGpkEnabled &&
          !isLoading &&
          isGpkPegawai &&
          periodYear >= 2026
        break

      case 'gpk-tim-kinerja':
        allowed = isTimKinerjaGpkAllowed && periodYear >= 2026
        break

      case 'gpk-pejabat-penilai-kinerja':
        allowed =
          !isLoading &&
          isKinerjaGpkEnabled &&
          isKepalaDinasEnabled &&
          isKepalaDinas &&
          periodYear >= 2026
        break
      // Fallback
      default:
        allowed = true
        break
    }

    return allowed
  })

  return (
    <>
      {filteredMenus?.map((menu, idx) => {
        // Get the index of the menu
        const index = idx + 1

        // Generate a unique id for each menu
        const id = [...(!!parentId ? [parentId] : []), index].join('-')

        // Generate a unique key for each menu
        const key = [...(!!parentKey ? [parentKey] : []), menu.key].join('-')

        // Check if the menu is active
        const isActive =
          activeMenu === menu.key ||
          menu.submenus?.filter((submenu) => submenu.key === activeMenu)
            .length > 0

        // Consider the menu as a link if it has a href and no submenus
        const isLink = !!menu.href && !menu.submenus && !isActive

        // Check if the menu is expanded
        const isExpanded = isActive || expanded?.includes(menu.key)

        return (
          <Fragment key={key}>
            <ListItem
              id={`sidebar-menu-${id}`}
              data-testid={`sidebar-menu-${key}`}
              className={clsx('select-none cursor-pointer', className)}
              title={menu.label}
              as={isLink ? Link : 'a'}
              href={isLink ? menu.href : undefined}
              target={isLink && menu.externalLink ? '_blank' : undefined}
              rel={
                isLink && menu.externalLink
                  ? 'noopener noreferrer nofollow'
                  : undefined
              }
              iconLeft={
                <Icon as="i" color="default" fontSize="default">
                  {menu.icon}
                </Icon>
              }
              iconRight={
                menu.submenus && (
                  <Icon
                    as="i"
                    color="default"
                    fontSize="default"
                    className={clsx(
                      'transition-transform transform',
                      isExpanded && 'rotate-180'
                    )}
                  >
                    expand_more
                  </Icon>
                )
              }
              onClick={() => {
                if (!isLink) {
                  setExpanded((prev) => {
                    // If the menu is expanded, collapse it
                    if (prev?.includes(menu.key))
                      return prev?.filter((item) => item !== menu.key)
                    // If the menu is collapsed, expand it
                    else return [...(prev || []), menu.key]
                  })
                }
              }}
            >
              <Text
                variant={isActive ? 'body-bold' : 'body'}
                className="overflow-wrap-anywhere"
              >
                {menu.label}
              </Text>
            </ListItem>

            {isExpanded && menu.submenus && (
              <MenuLink
                expanded={expanded}
                setExpanded={setExpanded}
                menus={menu.submenus}
                activeMenu={activeMenu}
                className={className}
                parentId={index.toString()}
                parentKey={menu?.key?.toString()}
              />
            )}
          </Fragment>
        )
      })}
    </>
  )
}

const KinerjaSidebar: FC<KinerjaSidebarProps> = ({
  sticky = false,
  activeMenu,
}) => {
  const toggleValue = useToggleSdkContext()
  const isNotifikasiPSPegawaiEnabled =
    toggleValue?.['kinerja-ps-pegawai-notification-enabled']

  const isNotifikasiEnabled =
    process.env.NEXT_PUBLIC_SKP_NOTIFIKASI_ENABLED === 'true'
  const { data: dataNotificationList } = useAPIGetNotificationList({
    options: {
      staleTime: 60 * 60 * 1000, // 1 hour
      cacheTime: 60 * 60 * 1000, // 1 hour
      useErrorBoundary: false,
      enabled: isNotifikasiEnabled,
    },
  })
  const { data: dataNotificationPenilaiPSList } =
    useAPIGetPSPegawaiNotificationList({
      staleTime: 60 * 60 * 1000, // 1 hour
      cacheTime: 60 * 60 * 1000, // 1 hour
      useErrorBoundary: false,
      enabled: isNotifikasiPSPegawaiEnabled,
    })
  const notificationCount = dataNotificationList?.meta?.total ?? 0
  const notificationPSPegawaiCount =
    dataNotificationPenilaiPSList?.meta?.total ?? 0

  const {
    isPegawaiPengawasSekolah,
    isTimKinerjaPengawasSekolah,
    isPPKPengawasSekolah,
  } = useRoleContext()
  const isTimPenilaiPS = isTimKinerjaPengawasSekolah || isPPKPengawasSekolah
  const timPenilaiPSNotificationPath = `${
    isTimKinerjaPengawasSekolah
      ? PENGELOLAAN_KINERJA_TIM_KINERJA_PS_PATH
      : PENGELOLAAN_KINERJA_PEJABAT_PENILAI_KINERJA_PS_PATH
  }/notifikasi`

  const menus: Array<SidebarNavItem> = [
    {
      key: 'beranda',
      label: 'Beranda',
      icon: 'home',
      href: PENGELOLAAN_KINERJA_BERANDA_PATH,
    },
    {
      key: 'guru-pegawai',
      label: 'Guru',
      icon: 'supervisor_account_outline',
      submenus: [
        {
          key: 'guru-pegawai',
          label: 'Sebagai Pegawai',
          href: PENGELOLAAN_KINERJA_GURU_PATH,
        },
      ],
    },
    {
      key: 'pamong-pegawai',
      label: 'Pamong',
      icon: 'supervisor_account_outline',
      submenus: [
        {
          key: 'pamong-pegawai',
          label: 'Sebagai Pegawai',
          href: PENGELOLAAN_KINERJA_PAMONG_PATH,
        },
      ],
    },
    {
      key: 'gpk-pegawai',
      label: RoleLabel.GPK,
      icon: 'supervisor_account_outline',
      submenus: [
        {
          key: 'gpk-pegawai',
          label: 'Sebagai Pegawai',
          href: PENGELOLAAN_KINERJA_GPK_PEGAWAI_PATH,
        },
      ],
    },
    {
      key: 'ks-parent',
      label: 'Kepala Sekolah',
      icon: 'supervisor_account_outline',
      submenus: [
        {
          key: 'ks-pegawai',
          label: 'Sebagai Pegawai',
          href: PENGELOLAAN_KINERJA_KS_PEGAWAI_PATH,
        },
        {
          key: 'ks-penilai',
          label: 'Sebagai Pejabat Penilai Kinerja',
          href: PENGELOLAAN_KINERJA_KS_PENILAI_PATH,
        },
      ],
    },
    {
      key: 'kepala-spnf-parent',
      label: 'Kepala SPNF',
      icon: 'supervisor_account_outline',
      submenus: [
        {
          key: 'kepala-spnf-pegawai',
          label: 'Sebagai Pegawai',
          href: PENGELOLAAN_KINERJA_KSPNF_PEGAWAI_PATH,
        },
        {
          key: 'kepala-spnf-penilai',
          label: 'Sebagai Pejabat Penilai Kinerja',
          href: PENGELOLAAN_KINERJA_KSPNF_PENILAI_PATH,
        },
      ],
    },
    {
      key: 'ps-parent',
      label: RoleLabel.PS,
      icon: 'supervisor_account_outline',
      submenus: [
        {
          key: 'ps-pegawai',
          label: 'Sebagai Pegawai',
          href: PENGELOLAAN_KINERJA_PS_PEGAWAI_PATH,
        },
        {
          key: 'ps-penilai',
          label: 'Sebagai Tim Kinerja',
          href: PENGELOLAAN_KINERJA_PS_PATH,
        },
      ],
    },
    {
      key: 'penilik-parent',
      label: 'Penilik',
      icon: 'supervisor_account_outline',
      submenus: [
        {
          key: 'penilik-pegawai',
          label: 'Sebagai Pegawai',
          href: PENGELOLAAN_KINERJA_PENILIK_PEGAWAI_PATH,
        },
      ],
    },
    {
      key: 'kadin-parent',
      label: RoleLabel.DINAS,
      icon: 'supervisor_account_outline',
      submenus: [
        {
          key: 'kadin-pemberi-predikat',
          label: 'Pengelolaan Kinerja Guru',
          href: PENGELOLAAN_KINERJA_KADIN_PEMBERI_PREDIKAT_PATH,
        },
        {
          key: 'kadin-ks',
          label: 'Pengelolaan Kinerja Kepala Sekolah',
          href: PENGELOLAAN_KINERJA_KADIN_PATH,
        },
        {
          key: 'gpk-pejabat-penilai-kinerja',
          label: 'Pengelolaan Kinerja Guru Pendidikan Khusus',
          href: PENGELOLAAN_KINERJA_GPK_PEJABAT_PENILAI_KINERJA_PATH,
        },
        {
          key: 'pejabat-penilai-kinerja-ps',
          label: 'Pengelolaan Kinerja Pengawas Sekolah',
          href: PENGELOLAAN_KINERJA_PEJABAT_PENILAI_KINERJA_PS_PATH,
        },
        {
          key: 'pejabat-penilai-kinerja-penilik',
          label: 'Pengelolaan Kinerja Penilik',
          href: PENGELOLAAN_KINERJA_PPK_PENILIK_PATH,
        },
      ],
    },
    {
      key: 'tim-kinerja-parent',
      label: RoleLabel.TK,
      icon: 'supervisor_account_outline',
      submenus: [
        {
          key: 'tim-kinerja-ps',
          label: 'Pengelolaan Kinerja Pengawas Sekolah',
          href: PENGELOLAAN_KINERJA_TIM_KINERJA_PS_PATH,
        },
        {
          key: 'tim-kinerja-penilik',
          label: 'Pengelolaan Kinerja Penilik',
          href: PENGELOLAAN_KINERJA_TK_PENILIK_PATH,
        },
        {
          key: 'gpk-tim-kinerja',
          label: 'Pengelolaan Kinerja Guru Pendidikan Khusus',
          href: PENGELOLAAN_KINERJA_GPK_TIM_KINERJA_PATH,
        },
      ],
    },
    {
      key: 'notifikasi',
      label: `Notifikasi (${notificationCount})`,
      icon: 'notifications_none',
      href: PENGELOLAAN_KINERJA_NOTIFIKASI_PATH,
    },
    {
      key: 'notifikasi-ps-parent',
      label: `Notifikasi (${notificationCount + notificationPSPegawaiCount})`,
      icon: 'notifications_none',
      submenus: [
        {
          key: 'notifikasi-ps-pegawai',
          label: `Sebagai Pegawai (${
            isPegawaiPengawasSekolah
              ? notificationPSPegawaiCount
              : notificationCount
          })`,
          href: isPegawaiPengawasSekolah
            ? `${PENGELOLAAN_KINERJA_PS_PEGAWAI_PATH}/notifikasi`
            : PENGELOLAAN_KINERJA_NOTIFIKASI_PATH,
        },
        {
          key: 'notifikasi-ps-tim-kinerja',
          label: `Sebagai Tim Kinerja (${
            isTimPenilaiPS ? notificationPSPegawaiCount : notificationCount
          })`,
          href: isTimPenilaiPS
            ? timPenilaiPSNotificationPath
            : PENGELOLAAN_KINERJA_NOTIFIKASI_PATH,
        },
      ],
    },
    {
      key: 'riwayat',
      label: 'Riwayat',
      icon: 'schedule',
      href: PENGELOLAAN_KINERJA_RIWAYAT_PATH,
    },
    {
      key: 'bantuan',
      label: 'Butuh Bantuan',
      icon: 'help',
      href: PANDUAN_PENGELOLAAN_URL,
      externalLink: true,
    },
  ]

  const [expanded, setExpanded] = useState<Array<PageName>>(
    !!expandAllOnFirstLoad ? menus.map((menu) => menu.key) : null
  )

  useEffect(() => {
    if (!!activeMenu) {
      setExpanded((prev) => {
        if (prev?.includes(activeMenu)) return prev
        else return [activeMenu]
      })
    }
  }, [activeMenu])

  if (menus.length === 0) return null

  return (
    <nav
      className={clsx(
        'py-5 px-2 bg-white !w-64 flex-none self-stretch',
        sticky && 'fixed top-[4.25rem] left-0 bottom-0 z-10',
        'overflow-y-auto'
      )}
      style={{
        boxShadow: 'var(--elevation-01)',
      }}
    >
      <MenuLink
        expanded={expanded}
        setExpanded={setExpanded}
        menus={menus}
        activeMenu={activeMenu}
      />
    </nav>
  )
}

export default KinerjaSidebar
